import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { http } from "../lib/api";
import { Button } from "../components/ui/button";

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshCredits } = useAuth();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshCredits();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch {}
      if (tries < 10) {
        timer = setTimeout(poll, 2000);
      } else {
        setStatus("timeout");
      }
    };
    if (sessionId) poll();
    else setStatus("failed");
    return () => clearTimeout(timer);
  }, [sessionId, refreshCredits]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div data-testid="payment-success" className="w-full max-w-md bg-white/80 border border-[#E2DCD2] rounded-xl shadow-sm p-8 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-[#A6844A] mx-auto" />
            <p className="mt-4 text-[#5C564E]">Confirming your payment…</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-[#2E6B48] mx-auto" />
            <h1 className="font-serif text-2xl font-bold text-[#1B3A4B] mt-4">You're all set.</h1>
            <p className="mt-2 text-[#5C564E]">Your searches are ready. Saved quotes and clean PDFs are on.</p>
            <Link to="/"><Button className="mt-6 rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white">Start a quote</Button></Link>
          </>
        )}
        {["failed", "timeout"].includes(status) && (
          <>
            <XCircle className="w-12 h-12 text-[#9E2A2B] mx-auto" />
            <h1 className="font-serif text-2xl font-bold text-[#1B3A4B] mt-4">
              {status === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="mt-2 text-[#5C564E]">
              {status === "timeout" ? "It's taking a moment. Check your account shortly." : "No charge was made. You can try again."}
            </p>
            <Link to="/"><Button variant="outline" className="mt-6 rounded-full border-[#A6844A] text-[#1B3A4B]">Back to BidMark Quotes</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div data-testid="payment-cancel" className="w-full max-w-md bg-white/80 border border-[#E2DCD2] rounded-xl shadow-sm p-8 text-center">
        <XCircle className="w-12 h-12 text-[#9E2A2B] mx-auto" />
        <h1 className="font-serif text-2xl font-bold text-[#1B3A4B] mt-4">Checkout canceled</h1>
        <p className="mt-2 text-[#5C564E]">No charge was made.</p>
        <Link to="/"><Button className="mt-6 rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white">Back to BidMark Quotes</Button></Link>
      </div>
    </div>
  );
}
